import * as React from 'react'
import { connect, ConnectedProps } from 'react-redux'
import type { Location } from 'react-router-dom'
import {
  NavigateFunction,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'

import { INBOX_TYPES } from '@disco/constants/GroupTypes'
import { PLAYLIST, TRACK } from '@disco/constants/ItemTypes'
import {
  alterPlaylistGroups,
  alterTrackGroups,
} from '@disco/state-management/actions/ExplorerActions'
import { getSelectedLibrary } from '@disco/state-management/selectors/app'
import {
  getGroup,
  getGroupsWithNesting,
} from '@disco/state-management/selectors/groups'
import {
  getExplorerSelectedItems,
  getExplorerSelectedPane,
  getExplorerSelectedPlaylists,
  getExplorerSelectedTracks,
} from '@disco/state-management/selectors/selection'
import {
  sendPlaylistDownloadEventThrottled,
  sendTrackDownloadEventThrottled,
} from '@disco/utils/eventsApi'

import type { ExplorerPane } from 'types/explorer'
import { Group } from 'types/group'
import { Playlist } from 'types/playlist'
import type { State } from 'types/redux'
import { Track } from 'types/track'

import AddToChannelModal from 'components/common/custom-libraries/add-to-channel'
import DocumentHeadTags from 'components/document-head-tags'

import type { RootState } from 'store/configureStore'
import { getSelectionReturnPath } from 'utils/routing'

interface OwnProps {
  navigate: NavigateFunction
  location: Location
  searchParams: URLSearchParams
}

function mapStateToProps(state: RootState, props: OwnProps) {
  const selectedItems = getExplorerSelectedItems(state)
  const selectedTracks = getExplorerSelectedTracks(state)
  const selectedPlaylists = getExplorerSelectedPlaylists(state)
  return {
    groups: getGroupsWithNesting(state),
    sourceGroup: getGroup(state, {
      groupId: parseInt(props.searchParams.get('source_group'), 10),
    }),
    selectedLibrary: getSelectedLibrary(state),
    selectedItems: selectedItems,
    selectedTracks: selectedTracks.filter(
      (item: Track) => !item.outside_your_territories,
    ),
    selectedPane: getExplorerSelectedPane(state),
    selectedPlaylists,
  }
}

const mapDispatchToProps = {
  alterTrackGroups,
  alterPlaylistGroups,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

type PropsFromRedux = ConnectedProps<typeof connector>

type Props = PropsFromRedux & OwnProps

class AddToChannel extends React.Component<Props> {
  _onClose = () => {
    this.props.navigate(getSelectionReturnPath(this.props.location.pathname))
  }

  _getPlaylistId() {
    return this.props.selectedPane?.playlist_id
  }

  _sendDownloadEvents(items) {
    const { selectedLibrary } = this.props
    if (!selectedLibrary) return
    items.forEach(item => {
      if (item.type === TRACK) {
        sendTrackDownloadEventThrottled(item.id, 'in-app', {
          group_id: selectedLibrary.id,
          playlist_id: this._getPlaylistId(),
        })
      } else if (item.type === PLAYLIST) {
        sendPlaylistDownloadEventThrottled(
          item.playlist_id,
          undefined,
          selectedLibrary.id,
        )
      }
    })
  }

  _onAddToGroup = groupsToAdd => {
    const existingGroups = groupsToAdd
      .filter(groupToAdd => !groupToAdd.created)
      .map(groupToAdd => {
        return this.props.groups.find(group => group.id === groupToAdd.value)
      })

    if (existingGroups.length) {
      const { selectedTracks, selectedPlaylists } = this.props

      if (selectedTracks.length) {
        this.props.alterTrackGroups(selectedTracks, existingGroups)
      }

      if (selectedPlaylists.length) {
        this.props.alterPlaylistGroups(selectedPlaylists, existingGroups)
      }
    }

    if (
      this.props.selectedLibrary &&
      INBOX_TYPES.includes(this.props.selectedLibrary.group_type)
    ) {
      this._sendDownloadEvents(this.props.selectedTracks)

      this._sendDownloadEvents(this.props.selectedPlaylists)
    }

    this._onClose()
  }

  render() {
    return (
      <div>
        <DocumentHeadTags title="Add to channel | DISCO" />
        <AddToChannelModal
          groups={this.props.groups}
          onSubmit={this._onAddToGroup}
          onClose={this._onClose}
        />
      </div>
    )
  }
}

const ConnectedAddToChannel = connector(AddToChannel)

// Wrapper component to provide navigate function to the connected component
const AddToChannelWithRouter = (props: any) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  return (
    <ConnectedAddToChannel
      {...props}
      navigate={navigate}
      location={location}
      searchParams={searchParams}
    />
  )
}

export default AddToChannelWithRouter
